import { Ability } from '@casl/ability';
import {
  Schedule,
  Event,
  Subjects,
  Actions,
  ResultStatus,
  EditScheduleForm,
  editScheduleFormDefault,
  editScheduleFormSchema,
  EditEventForm,
  editEventFormDefault,
  editEventFormSchema,
  EditFieldCapacityOverrideForm,
  editFieldCapacityOverrideFormDefault,
  editFieldCapacityOverrideFormSchema,
  QueryResult,
  MultiSelectOption,
  ClaimOwnerCapacity,
  FieldCapacityByDay,
  FieldCapacityMode,
} from '@dispatch/shared/types';
import type { ResourceForm } from '@dispatch/shared/types';
import {
  SectionDivider,
  Form,
  RenderFuncProps,
  SectionWithMeta,
  Modal,
  CreateNewEntityButton,
  useNotification,
} from '@dispatch/web/ui';
import {
  useGetFieldCapacityOverrides,
  useUpsertFieldCapacityOverride,
} from '@dispatch/shared/hooks';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Stack, Typography } from '@mui/material';
import React, { useCallback, useContext, useEffect } from 'react';
import {
  Control,
  FieldErrors,
  FieldValues,
  UseFormSetValue,
  useForm,
  useWatch,
} from 'react-hook-form';
import { EditEvent } from './EditEvent';
import { EditSchedule } from './EditSchedule';
import { EditFieldCapacityOverride } from './EditFieldCapacityOverride';
import { GeneralInformation } from './GeneralInformation';
import ResourceEvents from './ResourceEvents';
import ResourceSchedule from './ResourceSchedule';
import FieldCapacityOverrides from './FieldCapacityOverrides';
import { ClaimCapacity } from './ClaimCapacity';
import { ResourceContext } from '../../ResourcesDetails';

export type GeneralTabProps = {
  eventsQuery?: QueryResult<Event[] | undefined, unknown>;
  schedulesQuery?: QueryResult<Schedule[] | undefined, unknown>;
  upsertEventStatus: ResultStatus;
  upsertScheduleStatus: ResultStatus;
  onScheduleSave: (values: EditScheduleForm) => void;
  onEventSave: (values: EditEventForm) => void;
  control: Control<FieldValues>;
  permissions: Ability<[Actions, Subjects]> | undefined;
  errors: FieldErrors;
  isNewResource: boolean;
  efficiencies: MultiSelectOption[];
  claimOwnerCapacity?: ClaimOwnerCapacity;
  fieldCapacityByDay?: FieldCapacityByDay;
  isAlwaysAvailable?: boolean;
  isReadOnly?: boolean;
  /**
   * Per-tenant kill-switch for the per-day field capacity model (ADO #94651).
   * Off (the default) renders the legacy static Field Capacity input and hides
   * the date-specific override section entirely.
   */
  isFieldCapacityByDayEnabled?: boolean;
  /** Forwarded to ClaimCapacity to pre-fill the day grid on a Static -> Per day switch. */
  setValue?: UseFormSetValue<ResourceForm>;
};

export function GeneralTab({
  eventsQuery,
  schedulesQuery,
  upsertEventStatus,
  upsertScheduleStatus,
  onScheduleSave,
  onEventSave,
  control,
  permissions,
  errors,
  isNewResource,
  efficiencies,
  claimOwnerCapacity,
  fieldCapacityByDay,
  isAlwaysAvailable,
  isReadOnly = false,
  isFieldCapacityByDayEnabled = false,
  setValue,
}: GeneralTabProps) {
  const { data } = useContext(ResourceContext);
  const resourceId = data?.integrationGUID || '';
  const { notifySuccess, notifyError } = useNotification();
  const {
    control: editScheduleControl,
    formState: editScheduleFormState,
    handleSubmit: handleEditScheduleSubmit,
    reset: resetEditSchedule,
  } = useForm<EditScheduleForm>({
    defaultValues: {
      ...editScheduleFormDefault,
    },
    resolver: yupResolver(editScheduleFormSchema),
  });

  const {
    control: editEventControl,
    formState: editEventFormState,
    handleSubmit: handleEditEventSubmit,
    reset: resetEditEvent,
    watch: watchEditEvent,
    getValues: getEditValues,
    setValue: setEditValue,
  } = useForm<EditEventForm>({
    defaultValues: {
      ...editEventFormDefault,
    },
    resolver: yupResolver(editEventFormSchema),
  });
  const hasUpsertSchedulesPermissions = permissions?.can?.(
    'upsert',
    'resource-schedules'
  );
  const hasUpsertEventsPermissions = permissions?.can?.(
    'upsert',
    'resource-events'
  );

  const handleEditEventSave = useCallback(() => {
    handleEditEventSubmit(onEventSave)();
  }, [handleEditEventSubmit, onEventSave]);
  const handleEditScheduleSave = useCallback(() => {
    handleEditScheduleSubmit(onScheduleSave)();
  }, [handleEditScheduleSubmit, onScheduleSave]);

  const [openEditSchedule, setOpenEditSchedule] = React.useState(false);
  const handleOpenEditSchedule = useCallback(() => {
    setIsEdit(false);
    resetEditSchedule(editScheduleFormDefault);
    setOpenEditSchedule(true);
  }, [resetEditSchedule]);

  const handleCloseEditSchedule = () => {
    setOpenEditSchedule(false);
  };

  const [openEditEvent, setOpenEditEvent] = React.useState(false);

  const handleOpenEditEvent = useCallback(() => {
    setIsEdit(false);
    resetEditEvent(editEventFormDefault);
    setOpenEditEvent(true);
  }, [resetEditEvent]);

  const handleCloseEditEvent = () => {
    setOpenEditEvent(false);
  };

  const [isEdit, setIsEdit] = React.useState(false); // if the user is editing event/schedule

  useEffect(() => {
    if (upsertScheduleStatus === 'success') {
      resetEditSchedule();
      handleCloseEditSchedule();
    }
  }, [upsertScheduleStatus, resetEditSchedule]);

  useEffect(() => {
    if (upsertEventStatus === 'success') {
      resetEditEvent();
      handleCloseEditEvent();
    }
  }, [upsertEventStatus, resetEditEvent]);

  const setEditEventForm = useCallback(
    (editEventForm: EditEventForm) => {
      resetEditEvent(editEventForm);
    },
    [resetEditEvent]
  );

  const setEditScheduleForm = useCallback(
    (editScheduleForm: EditScheduleForm) => {
      resetEditSchedule(editScheduleForm);
    },
    [resetEditSchedule]
  );

  // Kept off the wire entirely when the per-day flag is off -- a flag-off tenant
  // has no override authoring UI, so it should make no override calls either.
  // Date overrides are resolved by the server ONLY for resources in PER_DAY mode -- the mode is
  // checked before the override lookup. Offering the override editor to a STATIC resource would let
  // a dispatcher create rows that are silently never read, so it is gated on the mode, not just on
  // the tenant flag.
  const fieldCapacityMode = useWatch({
    control,
    name: 'fieldCapacityMode',
  }) as FieldCapacityMode | undefined;
  const isFieldCapacityByDayPerDay =
    isFieldCapacityByDayEnabled && fieldCapacityMode === 'PER_DAY';

  const fieldCapacityOverridesQuery = useGetFieldCapacityOverrides({
    resourceId,
    enabled: isFieldCapacityByDayPerDay,
  });

  const {
    control: editFieldCapacityOverrideControl,
    formState: editFieldCapacityOverrideFormState,
    handleSubmit: handleEditFieldCapacityOverrideSubmit,
    reset: resetEditFieldCapacityOverride,
  } = useForm<EditFieldCapacityOverrideForm>({
    defaultValues: {
      ...editFieldCapacityOverrideFormDefault,
    },
    resolver: yupResolver(editFieldCapacityOverrideFormSchema),
  });

  const hasUpsertFieldCapacityOverridePermissions = permissions?.can?.(
    'upsert',
    'resource'
  );

  const [openEditFieldCapacityOverride, setOpenEditFieldCapacityOverride] =
    React.useState(false);
  const [isEditFieldCapacityOverride, setIsEditFieldCapacityOverride] =
    React.useState(false);

  const handleOpenEditFieldCapacityOverride = useCallback(() => {
    setIsEditFieldCapacityOverride(false);
    resetEditFieldCapacityOverride(editFieldCapacityOverrideFormDefault);
    setOpenEditFieldCapacityOverride(true);
  }, [resetEditFieldCapacityOverride]);

  const handleCloseEditFieldCapacityOverride = () => {
    setOpenEditFieldCapacityOverride(false);
  };

  const setEditFieldCapacityOverrideForm = useCallback(
    (editFieldCapacityOverrideForm: EditFieldCapacityOverrideForm) => {
      resetEditFieldCapacityOverride(editFieldCapacityOverrideForm);
    },
    [resetEditFieldCapacityOverride]
  );

  const onUpsertFieldCapacityOverrideResponse = useCallback(
    (response: { success: boolean; message?: string }) => {
      if (response?.success) {
        notifySuccess('Success', 'Field capacity override was saved');
        fieldCapacityOverridesQuery?.refetch &&
          fieldCapacityOverridesQuery.refetch();
        resetEditFieldCapacityOverride();
        handleCloseEditFieldCapacityOverride();
      } else {
        notifyError(
          'Error',
          response?.message || 'Error saving field capacity override'
        );
      }
    },
    [
      fieldCapacityOverridesQuery,
      notifyError,
      notifySuccess,
      resetEditFieldCapacityOverride,
    ]
  );

  const { upsertFieldCapacityOverride, upsertFieldCapacityOverrideStatus } =
    useUpsertFieldCapacityOverride(
      resourceId,
      onUpsertFieldCapacityOverrideResponse
    );

  const handleEditFieldCapacityOverrideSave = useCallback(() => {
    handleEditFieldCapacityOverrideSubmit(
      (values: EditFieldCapacityOverrideForm) => {
        upsertFieldCapacityOverride({
          date: values.date as string,
          capacity: Number(values.capacity),
        });
      }
    )();
  }, [handleEditFieldCapacityOverrideSubmit, upsertFieldCapacityOverride]);

  const renderWorkdaySection = useCallback(() => {
    return (
      <SectionWithMeta
        title="Work Days"
        meta={() => (
          <Box hidden={!hasUpsertSchedulesPermissions}>
            <CreateNewEntityButton
              onClick={handleOpenEditSchedule}
              entityName="Schedule"
              testId="edit-schedule-button"
              disabled={isNewResource || isReadOnly}
            />
          </Box>
        )}
      >
        <Modal
          testId="cert-popper-schedule"
          open={openEditSchedule}
          handleClickAway={handleCloseEditSchedule}
          title={'Work Days'}
        >
          <Form<EditScheduleForm>
            formId={'editScheduleForm'}
            errors={editScheduleFormState.errors}
            control={editScheduleControl}
            handleSubmit={handleEditScheduleSubmit}
            render={({ control, errors }: RenderFuncProps) => {
              return (
                <EditSchedule
                  control={control}
                  errors={errors}
                  onSubmit={handleEditScheduleSave}
                  upsertScheduleStatus={upsertScheduleStatus}
                  isEdit={isEdit}
                  permissions={permissions}
                />
              );
            }}
          />
        </Modal>
        <ResourceSchedule
          schedulesQuery={schedulesQuery}
          permissions={permissions}
          setOpenEditSchedule={setOpenEditSchedule}
          setEditScheduleForm={setEditScheduleForm}
          setIsEdit={setIsEdit}
          isReadOnly={isReadOnly}
        />
      </SectionWithMeta>
    );
  }, [
    editScheduleControl,
    editScheduleFormState.errors,
    schedulesQuery,
    handleEditScheduleSave,
    handleEditScheduleSubmit,
    handleOpenEditSchedule,
    hasUpsertSchedulesPermissions,
    isEdit,
    isNewResource,
    openEditSchedule,
    permissions,
    setEditScheduleForm,
    upsertScheduleStatus,
  ]);

  const renderBlockedTimeSection = useCallback(() => {
    return (
      <SectionWithMeta
        title="Blocked Time"
        meta={() => (
          <Box hidden={!hasUpsertEventsPermissions}>
            <CreateNewEntityButton
              onClick={handleOpenEditEvent}
              entityName="Event"
              testId="edit-event-button"
              disabled={isNewResource || isReadOnly}
            />
          </Box>
        )}
      >
        <Modal
          testId="cert-popper"
          open={openEditEvent}
          handleClickAway={handleCloseEditEvent}
          title={'Blocked Time'}
        >
          <Form<EditEventForm>
            formId={'editEventForm'}
            errors={editEventFormState.errors}
            control={editEventControl}
            handleSubmit={handleEditEventSubmit}
            render={({ control, errors }: RenderFuncProps) => {
              return (
                <EditEvent
                  control={control}
                  errors={errors}
                  onSubmit={handleEditEventSave}
                  upsertEventStatus={upsertEventStatus}
                  isEdit={isEdit}
                  watchEditEvent={watchEditEvent}
                  getEditValues={getEditValues}
                  setEditValues={setEditValue}
                />
              );
            }}
          />
        </Modal>
        <ResourceEvents
          eventsQuery={eventsQuery}
          permissions={permissions}
          setOpenEditEvent={setOpenEditEvent}
          setEditEventForm={setEditEventForm}
          setIsEdit={setIsEdit}
          isReadOnly={isReadOnly}
        />
      </SectionWithMeta>
    );
  }, [
    editEventControl,
    editEventFormState.errors,
    eventsQuery,
    handleEditEventSave,
    handleEditEventSubmit,
    handleOpenEditEvent,
    hasUpsertEventsPermissions,
    isEdit,
    isNewResource,
    openEditEvent,
    permissions,
    setEditEventForm,
    upsertEventStatus,
    watchEditEvent,
  ]);

  const renderFieldCapacityOverridesSection = useCallback(() => {
    return (
      <SectionWithMeta
        title="Field Capacity Overrides"
        meta={() => (
          <Box hidden={!hasUpsertFieldCapacityOverridePermissions}>
            <CreateNewEntityButton
              onClick={handleOpenEditFieldCapacityOverride}
              entityName="Override"
              testId="edit-field-capacity-override-button"
              disabled={isNewResource || isReadOnly}
            />
          </Box>
        )}
      >
        <Modal
          testId="field-capacity-override-popper"
          open={openEditFieldCapacityOverride}
          handleClickAway={handleCloseEditFieldCapacityOverride}
          title={'Field Capacity Override'}
        >
          <Form<EditFieldCapacityOverrideForm>
            formId={'editFieldCapacityOverrideForm'}
            errors={editFieldCapacityOverrideFormState.errors}
            control={editFieldCapacityOverrideControl}
            handleSubmit={handleEditFieldCapacityOverrideSubmit}
            render={({ control, errors }: RenderFuncProps) => {
              return (
                <EditFieldCapacityOverride
                  control={control}
                  errors={errors}
                  onSubmit={handleEditFieldCapacityOverrideSave}
                  upsertFieldCapacityOverrideStatus={
                    upsertFieldCapacityOverrideStatus
                  }
                  isEdit={isEditFieldCapacityOverride}
                />
              );
            }}
          />
        </Modal>
        <Typography component="p" fontSize="12px" paddingLeft="25px">
          An override replaces the day-of-week field capacity for that date.
        </Typography>
        <FieldCapacityOverrides
          resourceId={resourceId}
          overridesQuery={fieldCapacityOverridesQuery}
          permissions={permissions}
          setEditOverrideForm={setEditFieldCapacityOverrideForm}
          setOpenEditOverride={setOpenEditFieldCapacityOverride}
          setIsEdit={setIsEditFieldCapacityOverride}
          isReadOnly={isReadOnly}
        />
      </SectionWithMeta>
    );
  }, [
    editFieldCapacityOverrideControl,
    editFieldCapacityOverrideFormState.errors,
    fieldCapacityOverridesQuery,
    handleEditFieldCapacityOverrideSave,
    handleEditFieldCapacityOverrideSubmit,
    handleOpenEditFieldCapacityOverride,
    hasUpsertFieldCapacityOverridePermissions,
    isEditFieldCapacityOverride,
    isNewResource,
    isReadOnly,
    openEditFieldCapacityOverride,
    permissions,
    resourceId,
    setEditFieldCapacityOverrideForm,
    upsertFieldCapacityOverrideStatus,
  ]);

  const renderSections = useCallback(() => {
    return (
      <>
        {!isAlwaysAvailable && (
          <>
            <SectionDivider />
            <Stack>{renderWorkdaySection()}</Stack>
          </>
        )}
        <SectionDivider />
        <Stack>{renderBlockedTimeSection()}</Stack>
        {isFieldCapacityByDayPerDay && (
          <>
            <SectionDivider />
            <Stack>{renderFieldCapacityOverridesSection()}</Stack>
          </>
        )}
      </>
    );
  }, [
    isAlwaysAvailable,
    isFieldCapacityByDayPerDay,
    renderBlockedTimeSection,
    renderFieldCapacityOverridesSection,
    renderWorkdaySection,
  ]);

  return (
    <div
      style={{
        padding: '24px',
      }}
    >
      <GeneralInformation
        control={control}
        permissions={permissions}
        errors={
          errors && errors['generalInformation']
            ? errors['generalInformation']
            : null
        }
        efficiencies={efficiencies}
        isReadOnly={isReadOnly}
      />
      <SectionDivider />
      <ClaimCapacity
        control={control}
        errors={errors}
        claimOwnerCapacity={claimOwnerCapacity}
        fieldCapacityByDay={fieldCapacityByDay}
        isReadOnly={isReadOnly}
        isFieldCapacityByDayEnabled={isFieldCapacityByDayEnabled}
        setValue={setValue}
      />
      {renderSections()}
    </div>
  );
}
